import {
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  InputRef,
  Row,
  Select,
  Skeleton,
  Space,
  Table,
  TableColumnType,
  Tooltip,
} from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import { useClient } from "../../Context/ClientContext";
import { notificationContext } from "../../Common/Routing/PageRoute";
import OrganisationsService, {
  IOrganiseData,
} from "../../Services/Organisations/OrganisationsService";
import {
  LedgerStatus,
  LedgerStatusDropDown,
} from "../../Services/LedgerService";
import { ColumnsType } from "antd/es/table";
import { useTheme } from "../../Context/ThemeContext";
import { IIdNameModel } from "../../Services/ItemsService";
import { IApiResponse } from "../../../Utilities/ApiUtility.axios";
import AddOrganisationModel from "./AddOrganisationModel";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import {
  NumberSearchTableEnum,
  NumberSearchTableOption,
  TextSearchTableEnum,
  TextSearchTableOption,
} from "../../Common/SearchTableOptions";
import { tablePageSizeOptions } from "../../Common/ThemeSetting";
import "../../Common/Acc_Common.css";
import { FilterDropdownProps } from "antd/es/table/interface";

export interface OrgDataType {
  key?: string;
  sno: number;
  id: string;
  orgName: string;
  status: LedgerStatus;
}
const Organisations = () => {
  type DataIndex = keyof OrgDataType;
  const { themeData } = useTheme();
  const { currentClient } = useClient();
  const searchInput = useRef<InputRef>(null);
  const { openNotification } = useContext(notificationContext);
  const [openModel, setOpenModel] = useState<{ type: string; param: any }>({
    type: "",
    param: {},
  });
  const [trigger, setTrigger] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [orgList, setOrgList] = useState<OrgDataType[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(
    themeData?.tablePageSize ?? 15
  );
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [filter, setfilter] = useState<any>({
    sortCol: "",
    sortDir: "",
  });
  const [searchColumns, setSearchColumns] = useState<any>({
    orgName: "",
  });
  const [searchColumnCount, setSearchColumnCount] = useState<number>(0);
  const [searchColumnsDropDown, setSearchColumnsDropDown] = useState<{
    orgNameDropEnum: TextSearchTableEnum;
    orgNameDropPre: TextSearchTableEnum;
  }>({
    orgNameDropEnum: TextSearchTableEnum.contains,
    orgNameDropPre: TextSearchTableEnum.contains,
  });
  const [openDropdown, setOpenDropdown] = useState<DataIndex | null>(null);
  const [ledgerStatus, setLedgerStatus] = useState<LedgerStatus>(
    LedgerStatus.active
  );

  // Table Column Search
  const checkFilterType = (dataIndex: string) => {
    let isFilterTypeUnchanged = false;
    switch (dataIndex) {
      case "orgName":
        isFilterTypeUnchanged =
          searchColumnsDropDown.orgNameDropEnum ===
          searchColumnsDropDown.orgNameDropPre;
        break;
    }
    return isFilterTypeUnchanged;
  };

  const handleSearch = (
    selectedKeys: string[] | any,
    confirm: any,
    dataIndex: DataIndex
  ) => {
    let prevValue = searchColumns[dataIndex];
    let newValue = selectedKeys;

    // text/number inputs -> pick first value and trim
    if (dataIndex === "orgName") {
      newValue =
        selectedKeys && selectedKeys[0] ? String(selectedKeys[0]).trim() : "";
    }

    if (newValue === "" && (!prevValue || String(prevValue).trim() === "")) {
      confirm();
      setOpenDropdown(null);
      return;
    }
    // debugger;
    // text/number: unchanged value -> do nothing
    if (
      typeof prevValue === "string" &&
      newValue === String(prevValue).trim() &&
      checkFilterType(dataIndex)
    ) {
      confirm();
      setOpenDropdown(null);
      return;
    }

    setSearchColumnsDropDown((prevState) => {
      const newState = { ...prevState };
      switch (dataIndex) {
        case "orgName":
          newState.orgNameDropPre = prevState.orgNameDropEnum;
          break;
      }
      return newState;
    });

    let newSearchColumns = { ...searchColumns };

    switch (dataIndex) {
      case "orgName":
        newSearchColumns[dataIndex] = newValue || "";
        break;
    }

    const wasEmpty =
      !prevValue ||
      (Array.isArray(prevValue)
        ? prevValue.length === 0
        : String(prevValue).trim() === "");
    const isNowPopulated =
      (Array.isArray(newValue) && newValue.length > 0) ||
      (!Array.isArray(newValue) && newValue !== "");

    if (wasEmpty && isNowPopulated) {
      setSearchColumnCount((prev) => prev + 1);
    } else if (!wasEmpty && !isNowPopulated) {
      setSearchColumnCount((prev) => prev - 1);
    }

    setSearchColumns(newSearchColumns);
    confirm();
    setOpenDropdown(null);
  };

  const handleDropdownText = (
    dataIndex: DataIndex,
    valDropDown: TextSearchTableEnum
  ) => {
    switch (dataIndex) {
      case "orgName":
        setSearchColumnsDropDown({
          ...searchColumnsDropDown,
          orgNameDropEnum: valDropDown,
          orgNameDropPre: searchColumnsDropDown.orgNameDropEnum,
        });
        break;
    }
  };

  const handleReset = (
    clearFilters: () => void,
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    clearFilters();
    const prevValue = searchColumns[dataIndex];
    if (prevValue) {
      setSearchColumnCount((prev) => prev - 1);

      setSearchColumns((prev: any) => ({
        ...prev,
        [dataIndex]: "",
      }));
    }

    confirm();
    setOpenDropdown(null);
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex,
    isNumber: boolean,
    searchPlaceHolder?: string
  ): TableColumnType<OrgDataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* {dataIndex === "gstRate" ? (
          <Checkbox.Group
            options={companyGstAndNotGst?.map((gst: any) => ({
              key: gst.name,
              value: gst.id,
              title: gst.rate,
              label: gst.name,
            }))}
            style={{
              marginBottom: 8,
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
            value={selectedKeys}
            onChange={(e: any) => {
              setSelectedKeys(e ? [...e] : []);
            }}
          />
        ) :  ( */}

        <>
          {isNumber ? (
            <Select
              defaultValue={NumberSearchTableEnum.equalTo}
              style={{
                width: "100%",
                maxWidth: "200px",
                marginBottom: "8px",
              }}
              // onChange={(val: NumberSearchTableEnum) => {
              //   handleDropdownNumber(dataIndex, val);
              // }}
              options={NumberSearchTableOption?.map((x: any) => ({
                value: x.value,
                label: x.label,
              }))}
            />
          ) : (
            <Select
              defaultValue={TextSearchTableEnum.contains}
              style={{
                width: "100%",
                maxWidth: "200px",
                marginBottom: "8px",
              }}
              onChange={(val: TextSearchTableEnum) => {
                handleDropdownText(dataIndex, val);
              }}
              options={TextSearchTableOption?.map((x: any) => ({
                value: x.value,
                label: x.label,
              }))}
            />
          )}
          <Input
            ref={searchInput}
            placeholder={`Search.. ${searchPlaceHolder}`}
            value={selectedKeys[0]}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : []);
            }}
            onPressEnter={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            // onKeyPress={(e: any) => {
            //   if (dataIndex === "purchasePrice" || dataIndex === "salesPrice") {
            //     if (!/\d|[.,]/.test(e.key)) e.preventDefault();
            //     if (e.key === "." && e.target.value.includes("."))
            //       e.preventDefault();
            //   }
            // }}
            style={{ marginBottom: 8, display: "block", width: "200px" }}
          />
        </>

        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            size="small"
            style={{ width: 95 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters, confirm, dataIndex);
            }}
            size="small"
            style={{ width: 95 }}
          >
            Clear
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) =>
      searchPlaceHolder ? (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ) : (
        <FilterOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
    filterDropdownOpen: openDropdown === dataIndex,
    filterDropdownProps: {
      placement: "bottomLeft",
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setOpenDropdown(dataIndex);
        // setTimeout(() => {
        //   if (dataIndex === "gstRate") {
        //     searchSelect.current?.focus();
        //   } else {
        //     searchInput.current?.select();
        //   }
        // }, 100);
      } else {
        setOpenDropdown(null);
      }
    },
  });

  const getOrgList = async () => {
    const requestModel = {
      orgName: searchColumns.orgName?.trim(),
      orgNameDropEnum: searchColumnsDropDown.orgNameDropEnum,
    };
    setDataLoading(true);
    await OrganisationsService.getOrganisationsList(
      currentClient?.id!,
      (page - 1) * pageSize,
      pageSize,
      searchText,
      filter.sortCol,
      filter.sortDir,
      ledgerStatus,
      searchColumnCount,
      requestModel
    )
      .then((res) => {
        console.log("res", res);
        if (res?.items) {
          setOrgList(
            res.items?.map((org: IOrganiseData) => {
              return {
                sno: org?.sno,
                id: org?.id,
                orgName: org?.orgName,
                status: org?.status,
              };
            })
          );
          setTotalRecords(res?.totalRecords);
        }
      })
      .catch((ex: any) => {
        console.error(ex.message);
        setDataLoading(false);
        setOrgList([]);
      });
    setDataLoading(false);
  };

  const handleDelete = async (selId: string) => {
    if (selId) {
      await OrganisationsService.deleteOrganisation(currentClient?.id!, selId)
        .then((val: IApiResponse<IIdNameModel>) => {
          if (val?.status) {
            openNotification(
              "success",
              val?.result?.name + " organisation deleted successfully"
            );
            setTrigger((prev) => !prev);
          } else {
            openNotification("error", val?.errors && val?.errors[0]?.message);
          }
        })
        .catch((err) => {
          openNotification("error", err?.message || "");
        });
      setOpenModel({
        type: "",
        param: {},
      });
    }
  };

  useEffect(() => {
    if (currentClient?.id) {
      getOrgList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentClient?.id,
    trigger,
    searchText,
    page,
    pageSize,
    filter,
    searchColumns,
    ledgerStatus,
  ]);

  const columns: ColumnsType<OrgDataType> = [
    {
      title: "S.No.",
      dataIndex: "sno",
      key: "sno",
      width: "10%",
      render: (value: number) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Organisation Name",
      dataIndex: "orgName",
      sorter: true,
      defaultSortOrder: "ascend",
      onFilter: (value: any, record) => true,
      filteredValue: searchColumns.orgName ? [searchColumns.orgName] : null,
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
      ...getColumnSearchProps("orgName", false, "Organisation Name"),
    },
    {
      title: "Action",
      dataIndex: "id",
      width: "10%",
      render: (_, record) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              {record.status !== LedgerStatus.deleted && (
                <Tooltip
                  title={
                    record?.status === LedgerStatus.active
                      ? "In-Active"
                      : "Active"
                  }
                  placement="top"
                  color="#3D4998"
                >
                  <Checkbox
                    checked={record?.status !== LedgerStatus.deactive}
                    title="Delete"
                    onChange={(e: any) => {
                      // console.log(e.target.checked, record);
                      setOpenModel({
                        type: "isActive",
                        param: {
                          name: record.orgName,
                          id: record.id,
                          itemStatus: record?.status,
                        },
                      });
                      // setSelectChange(record?.isActive ? "In-Active" : "Active");
                    }}
                  />
                </Tooltip>
              )}
            </div>
            <div>
              {record.status === LedgerStatus.active ? (
                <Tooltip title="Edit" placement="top" color="#3D4998">
                  <EditOutlined
                    style={{
                      color: "#444653",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setOpenModel({
                        type: "addOrganisation",
                        param: {
                          id: record.id,
                        },
                      });
                    }}
                  />
                </Tooltip>
              ) : (
                <></>
              )}
            </div>
            <div>
              <Tooltip title="Delete" placement="top" color="red">
                <DeleteOutlined
                  style={{
                    color: "red",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setOpenModel({
                      type: "delete",
                      param: {
                        name: record.orgName,
                        id: record.id,
                      },
                    })
                  }
                />
              </Tooltip>
            </div>
          </div>
        </Skeleton>
      ),
    },
  ];

  //----- Table Ascending & Desending Order -----
  const tableSort = (pagination?: any, sort?: any, filter?: any) => {
    setfilter({
      sortCol: filter.column !== undefined ? filter.columnKey : "name",
      sortDir:
        filter.column !== undefined
          ? filter.order === "descend"
            ? "desc"
            : "asc"
          : "desc",
    });
    // console.log("sort1111111111111", sort?.TaxRate?.Rate, sort);
  };

  return (
    <Card
      size="small"
      className="TableCard"
      style={{
        marginTop: 35,
        boxShadow: "0px 0px 10px 0px #96A1C84D",
      }}
    >
      <Row justify="space-between">
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() =>
              setOpenModel({
                type: "addOrganisation",
                param: {},
              })
            }
          >
            Organisation
          </Button>
        </Col>
        <Col>
          <Row gutter={8}>
            <Col>
              <Input
                value={searchText}
                allowClear
                style={{ width: "200px", border: "1px solid #f1f3ff" }}
                placeholder="Search.."
                onChange={(val: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchText(val?.target?.value)
                }
              />
            </Col>
            <Col>
              <Select
                style={{ width: "110px" }}
                defaultValue={LedgerStatus.active}
                onSelect={(e) => {
                  setLedgerStatus(e);
                  // setExportItemsData([]);
                  setPage(1);
                }}
                options={LedgerStatusDropDown}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={
          dataLoading
            ? (Array.from({ length: pageSize }, (_, index) => ({
                key: `loading-${index}`,
              })) as OrgDataType[])
            : orgList
        }
        locale={{
          triggerDesc: "",
          triggerAsc: "",
          cancelSort: "",
        }}
        onChange={tableSort}
        className={`Tabel-style table-${themeData?.componentSize ?? "middle"}`}
        // rowSelection={rowSelection}
        pagination={{
          total: totalRecords,
          current: page,
          pageSize: pageSize,
          showSizeChanger: true,
          onChange: (page: number, pageSize: number) => {
            setPage(page);
            setPageSize(pageSize);
            // setExportType(-1);
          },
          pageSizeOptions: tablePageSizeOptions,
          showTotal: (totalRecords, page) =>
            `${page[0]}-${page[1]} of ${totalRecords} items`,
        }}
        // scroll={
        //   pageSize > 15 ? { x: 1000, y: window.innerHeight - 320 } : { x: 1000 }
        // }
      />
      {openModel.type === "addOrganisation" && (
        <AddOrganisationModel
          isModalOpen={true}
          setOpenModel={setOpenModel}
          id={openModel.param?.id}
          setTrigger={setTrigger}
        />
      )}
      {openModel.type === "delete" && (
        <ConfirmationModal
          open={true}
          onNo={() =>
            setOpenModel({
              type: "",
              param: {},
            })
          }
          onYes={() => handleDelete(openModel.param?.id)}
          text={`Are you sure you want to delete this row "${openModel.param?.name}" ?`}
        />
      )}
      {openModel.type === "isActive" && (
        <ConfirmationModal
          open={true}
          onNo={() => setOpenModel({ type: "", param: "" })}
          onYes={async () => {
            try {
              const apiData = await OrganisationsService.isActiveOrganisation(
                currentClient?.id!,
                openModel.param?.id,
                openModel.param?.itemStatus === LedgerStatus.deactive
                  ? LedgerStatus.active
                  : LedgerStatus.deactive
              );
              if (apiData.result) {
                openNotification(
                  "success",
                  `Organisation "${openModel?.param?.name}" ${
                    openModel?.param?.itemStatus === LedgerStatus.deactive
                      ? "Active"
                      : "InActive"
                  } sucessfully.`
                );

                setTrigger((x) => !x);
                setOpenModel({ type: "", param: "" });
              } else {
                openNotification("error", apiData?.message);
                console.log("error", apiData?.message);
                setOpenModel({ type: "", param: "" });
              }
            } catch (err) {
              openNotification("error", err);
              console.log("error", err);
              setOpenModel({ type: "", param: "" });
            }
          }}
          text={`Are you sure you want to ${
            openModel?.param?.itemStatus === LedgerStatus.deactive
              ? "Active"
              : "InActive"
          }  this "${openModel?.param?.name}" Organisation. ?`}
        />
      )}
    </Card>
  );
};

export default Organisations;
